import {
  ConnectionState,
  CurrencyCross,
  CurrencyCrossObj,
  EStreamCachedChartDTO,
  EStreamChartDTO,
  eStreamConnectionStateAtom,
  GetBETTradesResponse,
  InstrumentStoreItem,
  InstrumentSubscriptionDataScope,
  ListedInstrument,
  ListedInstrumentLike,
  ObjectSet,
  QuoteFeedInterval,
  SubscribeList,
  SubscribeOptions,
} from '@eih-netbroker/common';
import { useAtomValue } from 'jotai/index';
import { useCallback, useMemo } from 'react';
import { instrumentStore } from '../../data/instrumentStore';
import { SSE } from './SSE';

export const useEStream = () => {
  const eStreamConnectionState = useAtomValue(eStreamConnectionStateAtom);

  /**
   * Subscribes to EStream data based on the options
   * @param pOptions instruments to subscribe to
   * @param onInstrumentChange callback when the instrument data changes
   * @returns a function which will unsubscribe
   *
   */
  const subscribeToInstrumentChanges = useCallback(
    (
      pOptions: Array<SubscribeOptions> | SubscribeList,
      onInstrumentChange?: (items: ObjectSet<InstrumentStoreItem>) => void,
    ): (() => void) => {
      const options = Array.isArray(pOptions)
        ? pOptions
        : pOptions.instruments.map(instrument => ({
            instrument,
            source: pOptions.source,
            dataScope: pOptions.dataScope,
            forceRequestStaticData: pOptions.forceRequestStaticData,
          }));

      return SSE.sseCommunicator.subscribeToInstrumentChanges(options, onInstrumentChange);
    },
    [],
  );

  const hasEStreamConnection = useMemo(
    () => eStreamConnectionState === ConnectionState.Connected,
    [eStreamConnectionState],
  );

  /**
   * Loads the instrument data from EStream REST API
   */
  const loadInstrument = useCallback(
    (
      instruments: Array<ListedInstrument | ListedInstrumentLike>,
      dataScope: Array<InstrumentSubscriptionDataScope> = [InstrumentSubscriptionDataScope.MarketData],
    ): Promise<ObjectSet<InstrumentStoreItem>> => {
      return SSE.sseCommunicator.loadAndUpdateInstrumentWithSnapshot(instruments, dataScope);
    },
    [],
  );

  /**
   * Loads the instrument data from EStream REST API
   */
  const loadInstrumentSingle = useCallback(
    async (
      instrument: ListedInstrument | ListedInstrumentLike,
      dataScope: Array<InstrumentSubscriptionDataScope> = [InstrumentSubscriptionDataScope.MarketData],
    ): Promise<InstrumentStoreItem> => {
      const res = await loadInstrument([instrument], dataScope);
      const instrumentStoreItem = res.get(instrument);
      if (!instrumentStoreItem) {
        throw new Error(`Instrument ${instrument} not found`);
      }
      return instrumentStoreItem;
    },
    [loadInstrument],
  );

  /**
   *
   */
  const getInstrumentStoreItem = useCallback(
    (instrument: ListedInstrument | ListedInstrumentLike): InstrumentStoreItem | undefined => {
      return instrumentStore.get(instrument);
    },
    [],
  );

  const getBETTrades = useCallback(
    (fromDate: Date, limit?: number, fromMetaLastId?: string): Promise<GetBETTradesResponse> => {
      return SSE.sseCommunicator.eStreamConnection.getBETTrades(fromDate, limit, fromMetaLastId);
    },
    [],
  );

  const refreshEStreamMarketRights = useCallback(async () => {
    await SSE.sseCommunicator.connectEStreamSilent(true);
  }, []);

  const getChartData = useCallback(
    (
      ric: string,
      interval: QuoteFeedInterval,
      from: Date,
      to: Date,
      abortController?: AbortController,
    ): Promise<EStreamChartDTO> => {
      return SSE.sseCommunicator.getChartData(ric, interval, from, to, abortController);
    },
    [],
  );

  const getCachedIntraDayChart = useCallback((ric: string): Promise<EStreamCachedChartDTO> => {
    return SSE.sseCommunicator.getCachedIntraDayChart(ric);
  }, []);

  const getCachedInterDayChart = useCallback((ric: string): Promise<EStreamCachedChartDTO> => {
    return SSE.sseCommunicator.getCachedInterDayChart(ric);
  }, []);

  const getExchangeRate = useCallback(
    (currencyCrosses: Array<CurrencyCrossObj>): Promise<Map<CurrencyCross, number>> => {
      return SSE.sseCommunicator.getExchangeRate(currencyCrosses);
    },
    [],
  );

  return useMemo(
    () => ({
      subscribeToInstrumentChanges,
      loadInstrument,
      getBETTrades,
      refreshEStreamMarketRights,
      getChartData,
      getCachedIntraDayChart,
      getCachedInterDayChart,
      loadInstrumentSingle,
      hasEStreamConnection,
      getExchangeRate,
      getInstrumentStoreItem,
    }),
    [
      subscribeToInstrumentChanges,
      loadInstrument,
      getBETTrades,
      refreshEStreamMarketRights,
      getChartData,
      getCachedIntraDayChart,
      getCachedInterDayChart,
      loadInstrumentSingle,
      hasEStreamConnection,
      getExchangeRate,
      getInstrumentStoreItem,
    ],
  );
};
